/**
 * The game's HUD (Phase 5). Minimal by default: run time at the top, speed at
 * the bottom, mode and personal best in a corner. Checkpoint splits appear
 * under the timer with the difference to the best run's split. The expanded
 * layout adds a small readout panel (vertical speed, ground, surface, attempt,
 * deaths). Death and finish get a short centred banner; the death banner
 * carries a striped hazard bar, so it does not rely on colour alone.
 *
 * Presentation only: it reads simulation state and never writes it. Text
 * goes through textContent and is only written when it changes.
 */
import type { Simulation } from '../simulation/Simulation';
import { compareAt, type Split } from '../replay/RunTiming';
import type { FlashLevel, HudLayout } from './Settings';
import { formatTime, type HudView } from './Hud';

const MESSAGE_MS = 2200;
const SPLIT_MS = 3500;
const BANNER_MS = 1400;
/** Speed bar full scale, m/s. */
const SPEED_SCALE = 24;

export interface GameHudOptions {
  layout: HudLayout;
  flashes: FlashLevel;
}

function div(className: string, parent?: HTMLElement): HTMLDivElement {
  const d = document.createElement('div');
  d.className = className;
  parent?.append(d);
  return d;
}

/** Signed time difference, e.g. "−0.420" (ahead) or "+1.050" (behind). */
export function formatDelta(seconds: number): string {
  const sign = seconds < 0 ? '−' : '+';
  return `${sign}${Math.abs(seconds).toFixed(3)}`;
}

export class GameHud implements HudView {
  readonly root: HTMLDivElement;
  private readonly timer: HTMLDivElement;
  private readonly split: HTMLDivElement;
  private readonly speed: HTMLDivElement;
  private readonly speedFill: HTMLDivElement;
  private readonly mode: HTMLDivElement;
  private readonly best: HTMLDivElement;
  private readonly ghost: HTMLDivElement;
  private readonly message: HTMLDivElement;
  private readonly banner: HTMLDivElement;
  private readonly bannerText: HTMLDivElement;
  private readonly details: HTMLDListElement;
  private readonly flashEl: HTMLDivElement;
  private readonly last = new Map<HTMLElement, string>();
  private messageText = '';
  private messageUntil = 0;
  private splitUntil = 0;
  private bannerUntil = 0;
  private reference: readonly Split[] = [];
  private referenceLabel = 'PB';

  constructor(
    host: HTMLElement,
    private options: GameHudOptions,
  ) {
    this.root = div('game-hud');
    this.root.setAttribute('aria-live', 'polite');
    const top = div('hud-top', this.root);
    this.timer = div('hud-timer', top);
    this.split = div('hud-split', top);
    const corner = div('hud-corner', this.root);
    this.mode = div('hud-mode', corner);
    this.best = div('hud-best', corner);
    this.ghost = div('hud-ghost', corner);
    this.ghost.hidden = true;
    const bottom = div('hud-bottom', this.root);
    this.speed = div('hud-speed', bottom);
    const bar = div('hud-speedbar', bottom);
    this.speedFill = div('hud-speedfill', bar);
    this.message = div('hud-message', this.root);
    this.banner = div('hud-banner', this.root);
    div('hud-banner-stripe', this.banner);
    this.bannerText = div('hud-banner-text', this.banner);
    this.banner.hidden = true;
    this.details = document.createElement('dl');
    this.details.className = 'hud-details';
    this.root.append(this.details);
    this.flashEl = div('hud-flash', this.root);
    host.append(this.root);
    this.setOptions(options);
  }

  setOptions(options: GameHudOptions): void {
    this.options = options;
    this.root.dataset['layout'] = options.layout;
    this.root.dataset['flashes'] = options.flashes;
    this.details.hidden = options.layout !== 'expanded';
  }

  /** Splits of the run to compare against (the personal best, or the active ghost) and its short label. */
  setReference(splits: readonly Split[], label = 'PB'): void {
    this.reference = splits.map((s) => ({ ...s }));
    this.referenceLabel = label;
  }

  /** Names the active ghost ("Ghost · PB 41.230"); null hides the label. */
  setGhostLabel(text: string | null): void {
    this.ghost.hidden = text === null;
    this.ghost.textContent = text ?? '';
  }

  say(text: string, nowMs: number): void {
    this.messageText = text;
    this.messageUntil = nowMs + MESSAGE_MS;
  }

  flash(kind: 'death' | 'finish' | 'checkpoint'): void {
    if (this.options.flashes === 'off') return;
    this.flashEl.className = 'hud-flash';
    // Force a style flush so re-adding the class restarts the CSS animation.
    void this.flashEl.offsetWidth;
    this.flashEl.className = `hud-flash flash-${kind}`;
  }

  /** A short centred banner: "death" shows the hazard stripe, "invalid" a dashed one. */
  showBanner(kind: 'death' | 'finish' | 'invalid', text: string, nowMs: number): void {
    this.banner.dataset['kind'] = kind;
    this.set(this.bannerText, text);
    this.banner.hidden = false;
    this.bannerUntil = nowMs + BANNER_MS;
  }

  /**
   * The latest checkpoint split and its difference to the reference run at the same checkpoint. Only
   * compared when the reference reached the same checkpoints in the same order (RunTiming.compareAt).
   */
  showSplit(splits: readonly Split[], nowMs: number): void {
    const last = splits[splits.length - 1];
    if (!last) return;
    const cmp = compareAt(splits, this.reference);
    const delta = cmp ? ` ${formatDelta(cmp.delta)} ${this.referenceLabel}` : '';
    this.split.dataset['trend'] = cmp ? (cmp.delta <= 0 ? 'ahead' : 'behind') : 'none';
    this.set(this.split, `CP ${last.index + 1}  ${formatTime(last.time)}${delta}`);
    this.splitUntil = nowMs + SPLIT_MS;
  }

  update(sim: Simulation, nowMs: number, experiments: string): void {
    const a = sim.attempts;
    const s = sim.state;
    const elapsed = a.timer.elapsed(s.tick);
    // An interrupted full run keeps saying so until the player restarts (reaching the finish records nothing).
    this.set(this.timer, a.status === 'invalidated' ? 'NOT RECORDED · R restarts' : formatTime(elapsed ?? (a.status === 'ready' ? 0 : null)));
    this.timer.dataset['status'] = a.status;
    const speed = s.planarSpeed();
    this.set(this.speed, `${speed.toFixed(1)}`);
    this.speedFill.style.transform = `scaleX(${Math.min(1, speed / SPEED_SCALE).toFixed(3)})`;
    this.set(this.mode, `${a.mode === 'full-run' ? 'Full run' : 'Practice'}${a.status === 'invalidated' ? ' · invalidated' : a.status === 'failed' ? ' · failed' : ''}${experiments ? ` · exp ${experiments}` : ''}`);
    this.mode.dataset['mode'] = a.mode;
    this.set(this.best, `Best ${formatTime(a.bestTime())}`);
    this.split.hidden = nowMs >= this.splitUntil;
    this.message.hidden = nowMs >= this.messageUntil;
    if (!this.message.hidden) this.set(this.message, this.messageText);
    if (nowMs >= this.bannerUntil) this.banner.hidden = true;
    if (this.options.layout === 'expanded') this.updateDetails(sim);
  }

  dispose(): void {
    this.root.remove();
  }

  private updateDetails(sim: Simulation): void {
    const s = sim.state;
    const rows: [string, string][] = [
      ['Vertical', `${s.velocity.y.toFixed(1)} m/s`],
      ['Ground', s.grounded ? 'on ground' : 'in the air'],
      ['Surface', s.grounded ? (s.groundMaterial?.kind ?? 'safe') : '—'],
      ['Attempt', String(sim.attempts.attempt)],
      ['Deaths', String(sim.attempts.deathsThisAttempt)],
      ['Checkpoint', sim.attempts.lastCheckpointIndex === null ? '—' : String(sim.attempts.lastCheckpointIndex + 1)],
    ];
    const text = rows.map((r) => r.join(':')).join('|');
    if (this.last.get(this.details) === text) return;
    this.last.set(this.details, text);
    this.details.replaceChildren(
      ...rows.flatMap(([k, v]) => {
        const dt = document.createElement('dt');
        dt.textContent = k;
        const dd = document.createElement('dd');
        dd.textContent = v;
        return [dt, dd];
      }),
    );
  }

  private set(el: HTMLElement, text: string): void {
    if (this.last.get(el) === text) return;
    this.last.set(el, text);
    el.textContent = text;
  }
}
